/**
 * The API paths that BOTH halves of the application have to agree on, in the one place they can
 * both import from.
 *
 * WHAT WENT WRONG, Oct 2026. The server published the Swagger UI at `/docs` and the frontend's
 * "Open API documentation" button opened `<api base>/docs`. Both were self-consistent, every test
 * passed, and no partner could open the page — because in deployment the app and the API answer on
 * ONE origin, behind a reverse proxy that forwards only `/rest`, `/oauth` and `/webhooks` to the
 * API and serves everything else from the frontend's static build, falling back to `index.html` so
 * the app's own screens survive a deep link.
 *
 * That fallback is what made the bug invisible. A browser-facing API path outside those prefixes
 * does not 404 — it returns the APP, with a 200 and an HTML body. The partner who clicked the
 * button got the portal dashboard they were already looking at, and nothing on screen, in a log, or
 * in any test suite said the documentation had been swallowed on the way.
 *
 * So the rule, and it is not negotiable: ANYTHING THE API SERVES TO A BROWSER LIVES UNDER ONE OF
 * {@link BROWSER_REACHABLE_API_PREFIXES}. The server asserts it at boot (see
 * `assertBrowserReachable` in server/src/app.ts) and the frontend builds its urls from the
 * constants below, so the two cannot drift apart again.
 */

/**
 * Every path prefix the deployment's reverse proxy forwards to the API. Everything else on the
 * origin belongs to the frontend.
 */
export const BROWSER_REACHABLE_API_PREFIXES = ['/rest/', '/oauth/', '/webhooks/'] as const;

/** The Swagger UI — the partner-facing REST documentation, with Authorize and Try-it-out. */
export const DOCS_ROUTE_PREFIX = '/rest/docs';

/** Static assets the API itself serves to a browser (the vendored ReDoc bundle). */
export const VENDOR_ASSET_PREFIX = '/rest/vendor';

/** True when a path the API serves to a browser would actually reach the API in deployment. */
export function isBrowserReachableApiPath(path: string): boolean {
    return BROWSER_REACHABLE_API_PREFIXES.some((prefix) => path.startsWith(prefix));
}
